/** Client for the video scene edit loop (/api/video/spec/:name/…). */

export interface Caption { at: number; text: string }
export interface SceneSummary {
  idx: number;
  id: string;
  kind: string;
  variant?: string;
  vo: string;
  captions: Caption[];
  sec: number;
}
export interface VideoRevision { seq: number; kind: string; note?: string; source?: string }
export interface VideoHistory { headSeq: number; revisions: VideoRevision[] }
export interface ScenesState {
  name: string;
  title: string;
  scenes: SceneSummary[];
  history: VideoHistory;
}
export type PatchOp = { op: 'replace' | 'add' | 'remove'; path: string; value?: unknown };
export interface EditPreview { ok?: boolean; source?: 'direct' | 'ai'; patch?: PatchOp[]; scenes?: SceneSummary[]; title?: string; error?: string }

const base = (name: string) => `/api/video/spec/${encodeURIComponent(name)}`;

async function get<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: 'include' });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json() as Promise<T>;
}
async function post<T>(url: string, body: unknown): Promise<T> {
  const r = await fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, credentials: 'include', body: JSON.stringify(body) });
  const d = (await r.json().catch(() => ({}))) as T & { ok?: boolean; error?: string };
  if (!r.ok && d.ok === undefined) throw new Error(d.error || `${r.status}`);
  return d;
}

export const getScenes = (name: string) => get<ScenesState>(`${base(name)}/scenes`);
export const previewDirect = (name: string, patch: PatchOp[]) => post<EditPreview>(`${base(name)}/scene-edit`, { patch });
export const previewAi = (name: string, sceneIdx: number, instruction: string) => post<EditPreview>(`${base(name)}/scene-edit`, { sceneIdx, instruction });
export const commitScene = (name: string, patch: PatchOp[], note: string, source: 'direct' | 'ai') => post<ScenesState>(`${base(name)}/scene-commit`, { patch, note, source });
export const checkoutSpecRev = (name: string, seq: number) => post<ScenesState>(`${base(name)}/spec-checkout`, { seq });

/** Kick a scoped draft render of one scene and poll to a served URL. */
export async function renderScene(name: string, sceneId: string): Promise<string> {
  const kick = await post<{ jobId?: string; error?: string }>('/api/video/render', { name, previewScenes: [sceneId] });
  if (!kick.jobId) throw new Error(kick.error || 'render failed to start');
  for (;;) {
    await new Promise((r) => setTimeout(r, 2500));
    const p = await get<{ status: string; url?: string; log?: string }>(`/api/video/render/${kick.jobId}`);
    if (p.status === 'done' && p.url) return `${p.url}?t=${Date.now()}`;
    if (p.status === 'error') throw new Error('render failed');
  }
}
